import { Timeline } from "@/components/Timeline";
import { QuiltPlannerHeader } from "@/components/QuiltPlannerHeader";
import { QuiltPlannerFooter } from "@/components/QuiltPlannerFooter";
import { GlobalRecentPlans } from "@/components/GlobalRecentPlans";
import { useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import { Card, CardContent, Box, Typography, Container } from "@mui/material";
import { Bed, Weekend, MoneyOff, Celebration, Devices } from '@mui/icons-material';
import { cardContentSx } from '@/utils/sharedStyles';
const timelineSteps = [{
  number: 1,
  title: "Purpose",
  description: "Choose bed cover or throw blanket."
}, {
  number: 2,
  title: "Size",
  description: "Choose from international bed sizes. Or, add a custom throw blanket size."
}, {
  number: 3,
  title: "Design",
  description: "Add what size you want your finished blocks to be. Add sashing, cornerstones, and borders if you like."
}, {
  number: 4,
  title: "Fabric settings",
  description: "Change default settings like what size fabric you'll buy, how much it will cost, and the seam allowance."
}, {
  number: 5,
  title: "Get a quilt plan",
  description: "Bookmark the link or save it to your Notes app.",
  isCheck: true
}];
const LandingPage = () => {
  const navigate = useNavigate();
  const handleStartPlanning = (purpose: 'bed-cover' | 'throw-blanket') => {
    // Track Google Analytics event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'start_planning', {
        event_category: 'navigation',
        method: purpose,
        purpose: purpose
      });
    }
    navigate('/planner', {
      state: {
        quiltData: {
          purpose
        },
        initialStep: 1 // Start on step 2 (0-indexed)
      }
    });
  };
  const handleGetQuiltPlan = () => {
    // Track Google Analytics event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'start_planning', {
        event_category: 'navigation',
        method: 'get_plan_button'
      });
    }
    navigate('/planner', {
      state: {
        initialStep: 0 // Start on step 1 (0-indexed)
      }
    });
  };
  return <Box sx={{
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  }}>
        <QuiltPlannerHeader showClear={false} onClear={() => {}} />
      
      <Box component="main" sx={{
      flex: 1,
      backgroundColor: 'background.default'
    }}>
        {/* Hero Section */}
        <Container maxWidth="lg" sx={{
        px: 2,
        py: 6,
        textAlign: 'center'
      }}>
          <Box>
          <Typography variant="h1" component="h1" sx={{
            fontSize: {
              xs: '2.25rem',
              md: '3rem'
            },
            fontWeight: 'bold',
            color: 'text.primary',
            mb: 3
          }}>
            Quilt math, done for you.
          </Typography>
          
          <Typography variant="h2" component="h2" sx={{
            color: 'text.primary',
            fontWeight: 300,
            mb: 3,
            maxWidth: '48rem',
            mx: 'auto'
          }}>Don't know what size to make your quilt? How many blocks to make?<br />Start here.</Typography>
          
          
          <Box sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row'
            },
            gap: 2,
            justifyContent: 'center'
          }}>
            <Button variant="contained" color="primary" onClick={() => handleStartPlanning('bed-cover')} startIcon={<Bed aria-hidden="true" focusable="false" />} sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: '1.125rem',
              textTransform: 'none',
              width: {
                xs: '100%',
                sm: 'auto'
              },
              borderRadius: 2,
              minHeight: '44px'
            }}>Plan a bed cover</Button>
            <Button variant="contained" color="primary" onClick={() => handleStartPlanning('throw-blanket')} startIcon={<Weekend aria-hidden="true" focusable="false" />} sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: '1.125rem',
              textTransform: 'none',
              width: {
                xs: '100%',
                sm: 'auto'
              },
              borderRadius: 2,
              minHeight: '44px'
            }}>Plan a throw blanket</Button>
          </Box>
          
          <Typography variant="body2" sx={{
            color: 'text.secondary',
            mt: '20px',
            textAlign: 'center'
          }}>It's free. No accounts or downloads needed.</Typography>
          </Box>
        </Container>

       {/* Recent Plans Section */}
        <GlobalRecentPlans />
        
        {/* How it works section */}
        <Box sx={{
        py: 8,
        backgroundColor: 'background.default'
      }}>
          <Container maxWidth="lg" sx={{
          px: 2
        }}>
            <Typography variant="h2" component="h2" sx={{
            textAlign: 'center',
            mb: 2,
            fontSize: '2rem'
          }}>How to build a plan</Typography>
            
            <Typography variant="body1" sx={{
            color: 'text.secondary',
            textAlign: 'center',
            mb: 6
          }}>Fill out a simple 4-step form.</Typography>
            
            <Box sx={{
            mx: 'auto',
            position: 'relative',
            maxWidth: '500px'
          }}>
              <Timeline steps={timelineSteps} />
            </Box>
            
            <Box sx={{
            textAlign: 'center',
            mt: 4
          }}>
              <Button variant="contained" color="primary" onClick={handleGetQuiltPlan} sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: '1.125rem',
              textTransform: 'none',
              borderRadius: 2,
              minHeight: '44px'
            }}>
                Start quilt plan
              </Button>
            </Box>
          </Container>
        </Box>
        

        {/* Who's behind this quilt planner section */}
        <Box sx={{
        backgroundColor: 'primary.light',
        py: 8
      }}>
          <Container maxWidth="lg" sx={{
          px: 2
        }}>
            <Typography variant="h2" component="h2" sx={{
            textAlign: 'center',
            mb: 2,
            fontSize: '2rem'
          }}>
              Who's behind this quilt planner?
            </Typography>
            
            <Typography variant="body1" sx={{
            color: 'text.secondary',
            textAlign: 'center',
            mb: 6
          }}>It's a passion project.</Typography>
            
            <Box sx={{
            maxWidth: '64rem',
            mx: 'auto'
          }}>
              
              <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'auto 1fr'
              },
              gap: 4,
              alignItems: 'center',
              maxWidth: '750px',
              mx: 'auto'
            }}>
                <Box sx={{
                width: {
                  xs: '100%',
                  md: '260px'
                },
                aspectRatio: '1',
                overflow: 'hidden',
                borderRadius: 2
              }}>
                  <Box component="img" src={new URL('@/assets/emily-quilt.jpg', import.meta.url).href} alt="Emily's colorful quilt with star patterns" sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }} />
                </Box>
                
                <Box>
                  <Typography variant="h3" sx={{
                  mb: 2,
                  textAlign: 'left'
                }}>I'm Emily, a quilter &amp; user experience designer in Asheville, North Carolina.</Typography>
                  <Typography variant="body1" sx={{
                  color: 'text.primary',
                  textAlign: 'left'
                }}>
                    I created this quilt planner because I needed help with my quilt math and I wanted to learn how to use AI to build websites. I made it available for free because it's a fun project to work on and I think it's really useful! I don't make money from it or collect personal data. You can{' '}
                    <Box component="a" href="https://docs.google.com/forms/d/e/1FAIpQLScRJtzvGLaC22oTmgbU4Us7MTRIaOFjNdx3cU4_3HRNKp1hUg/viewform?usp=header" target="_blank" rel="noopener noreferrer" sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    '&:hover': {
                      textDecoration: 'none'
                    }
                  }}>
                      support this project by leaving feedback
                    </Box>. Happy quilting!
                  </Typography>
                </Box>
              
              </Box>
              
              
              
            </Box>
          </Container>
        </Box>
        
      </Box>

        <QuiltPlannerFooter />
      </Box>;
};
export default LandingPage;